import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Returns whether the current user's phone is verified.
 * - `null` while loading
 * - `true` if phone_verified_at is set
 * - `false` otherwise (including no user row)
 */
export function usePhoneVerified() {
  const [verified, setVerified] = useState<boolean | null>(null);

  useEffect(() => {
    const check = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setVerified(false);
        return;
      }

      const { data } = await supabase
        .from('users')
        .select('phone, phone_verified_at')
        .eq('auth_id', user.id)
        .maybeSingle();

      // Accept either our column or Supabase Auth's phone_confirmed_at — the latter
      // covers accounts created before the phone_verified_at migration.
      const verified = data?.phone_verified_at != null || user.phone_confirmed_at != null;

      // Backfill phone_verified_at so future checks don't need to hit auth.users.
      // Direct writes to phone_verified_at are revoked (F12 — it's a trust
      // flag, not a profile field), so this goes through the same RPC the
      // settings "change phone" flow uses. The RPC stamps ONLY the number
      // Supabase Auth confirmed for this caller (20261023171042), so pass that
      // number — auth's `user.phone` (bare digits, the RPC normalises it) —
      // not users.phone, which may hold an unconfirmed edit.
      if (user.phone_confirmed_at && user.phone && data && !data.phone_verified_at) {
        await supabase.rpc('set_verified_phone' as never, { p_phone: user.phone } as never);
      }

      setVerified(verified);
    };
    check();
  }, []);

  return verified;
}
